// Pemutar video layar penuh, dipakai video kenalan (promo.js) dan video materi modul belajar.
//   - Versi tegak untuk layar tegak (HP), lanskap untuk tablet/PC; ikut berganti bila HP diputar
//     dan melanjutkan dari detik yang sama (media fragment #t=).
//   - Bila browser menolak autoplay bersuara, video diputar tanpa suara dengan tombol suara.
//   - Beberapa video diputar berurutan; setelah video terakhir muncul tombol akhir + "Putar lagi".
//   - File yang sudah diputar disimpan utuh ke Cache Storage, lalu service worker melayaninya dari
//     cache (dengan dukungan Range): tonton ulang tidak mengunduh lagi dan tetap bisa saat offline.
//     Lihat runtimeCaching di vite.config.js (semua URL /video/).
import { esc } from '../core/format.js';

export const CACHE_VIDEO = 'video-kenalan';
const mqTegak = matchMedia('(orientation: portrait)');
const orientasi = () => (mqTegak.matches ? 'tegak' : 'lanskap');

// Simpan file video utuh ke Cache Storage (sekali per file).
async function simpanKeCache(url) {
  try {
    if (!('caches' in window)) return;
    const c = await caches.open(CACHE_VIDEO);
    if (await c.match(url)) return;
    const res = await fetch(url, { priority: 'low' });
    if (res.ok && res.status === 200) await c.put(url, res);
  } catch (e) { /* cache hanya pelengkap */ }
}

/** Apakah video (versi orientasi saat ini) sudah ada di cache, jadi bisa diputar offline? */
export async function videoTersimpan(v) {
  try { return 'caches' in window && !!(await (await caches.open(CACHE_VIDEO)).match(`${v[orientasi()]}.mp4`)); } catch (e) { return false; }
}

/**
 * Buka pemutar.
 * @param {object} o
 * @param {Array<{tegak: string, lanskap: string, judul?: string}>} o.daftar  URL tanpa ekstensi (.mp4/.jpg)
 * @param {string} o.label             label dialog untuk pembaca layar
 * @param {boolean} [o.webm]           sediakan juga sumber .webm (untuk browser tanpa codec H.264)
 * @param {string} [o.labelTutup]      teks tombol tutup (mis. "Lewati"); tanpa ini tombolnya hanya ✕
 * @param {{label: string, on: Function}} o.akhir  tombol utama setelah video terakhir selesai
 * @param {Function} [o.saatMain]      dipanggil (indeks) saat sebuah video mulai berputar
 * @param {Function} [o.saatTutup]     dipanggil ({ selesai, indeks }) saat pemutar ditutup
 * @param {HTMLElement} [o.asal]       elemen yang difokuskan lagi setelah ditutup
 */
export function bukaVideo({ daftar, label, webm = false, labelTutup = '', akhir, saatMain, saatTutup, asal }) {
  document.querySelector('.promo-layar')?.remove();
  const banyak = daftar.length > 1;
  const el = document.createElement('div');
  el.className = 'promo-layar';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-label', label);
  el.innerHTML = `<div class="promo-bingkai">
      <video playsinline controls preload="auto"></video>
      <p class="promo-judul" hidden></p>
      <button class="btn btn-sm promo-suara" type="button" hidden>🔊 Nyalakan suara</button>
      <div class="promo-akhir" hidden><button class="btn btn-go" type="button" data-utama>${esc(akhir.label)}</button><button class="btn btn-sm" type="button" data-ulang>Putar lagi</button></div>
      <p class="promo-galat" hidden>Videonya belum bisa diputar. Pastikan perangkat terhubung ke internet, lalu coba lagi.<br><button class="btn btn-sm btn-go" type="button" data-galat-lanjut>${esc(akhir.label)}</button></p>
    </div>
    <button class="promo-tutup${labelTutup ? ' berlabel' : ''}" type="button" aria-label="${esc(labelTutup || 'Tutup video')}">${labelTutup ? `${esc(labelTutup)} ✕` : '✕'}</button>`;
  document.body.append(el);
  const video = el.querySelector('video'), layarAkhir = el.querySelector('.promo-akhir'), galat = el.querySelector('.promo-galat');
  const tombolSuara = el.querySelector('.promo-suara'), judul = el.querySelector('.promo-judul');
  let indeks = 0, aktif = '', selesai = false;
  // Putar bersuara; bila browser menolak (autoplay tanpa ketukan), putar tanpa suara.
  const putar = () => video.play().catch(e => {
    if (e?.name !== 'NotAllowedError' || video.muted) return;
    video.muted = true;
    tombolSuara.hidden = false;
    video.play().catch(() => {});
  });
  const muat = (lanjut = 0, paksa = false) => {
    const o = orientasi(), v = daftar[indeks];
    if (o === aktif && !paksa) return;
    aktif = o;
    el.dataset.orientasi = o;
    video.poster = `${v[o]}.jpg`;
    const dari = lanjut ? `#t=${lanjut.toFixed(2)}` : '';
    video.innerHTML = `<source src="${v[o]}.mp4${dari}" type="video/mp4">${webm ? `<source src="${v[o]}.webm${dari}" type="video/webm">` : ''}`;
    judul.hidden = !v.judul;
    judul.textContent = v.judul ? `🎬 ${v.judul}${banyak ? ` · ${indeks + 1}/${daftar.length}` : ''}` : '';
    galat.hidden = true;
    layarAkhir.hidden = true;
    video.load();
    putar();
  };
  const ke = i => { indeks = i; muat(0, true); };
  const tutup = () => {
    mqTegak.removeEventListener('change', gantiOrientasi);
    document.removeEventListener('keydown', tombolEsc, true);
    video.pause();
    video.innerHTML = '';
    video.load();
    el.remove();
    saatTutup?.({ selesai, indeks });
    asal?.focus?.();
  };
  // HP diputar saat video berjalan: ganti ke versi yang cocok, lanjut dari detik yang sama.
  const gantiOrientasi = () => muat(video.currentTime);
  mqTegak.addEventListener('change', gantiOrientasi);
  video.addEventListener('ended', () => {
    if (indeks < daftar.length - 1) return ke(indeks + 1);
    selesai = true;
    layarAkhir.hidden = false;
    layarAkhir.querySelector('[data-utama]').focus();
  });
  video.addEventListener('playing', () => {
    galat.hidden = true; layarAkhir.hidden = true;
    saatMain?.(indeks);
    simpanKeCache(video.currentSrc.split('#')[0]);
  });
  video.addEventListener('volumechange', () => { if (!video.muted) tombolSuara.hidden = true; });
  // Bila semua <source> gagal, galat muncul di <source> terakhir (bukan di <video>).
  video.addEventListener('error', () => { galat.hidden = false; }, true);
  tombolSuara.onclick = () => { video.muted = false; tombolSuara.hidden = true; video.play().catch(() => {}); };
  layarAkhir.querySelector('[data-ulang]').onclick = () => { selesai = false; if (banyak) ke(0); else { layarAkhir.hidden = true; video.currentTime = 0; putar(); } };
  const keAkhir = () => { tutup(); akhir.on?.(); };
  layarAkhir.querySelector('[data-utama]').onclick = keAkhir;
  galat.querySelector('[data-galat-lanjut]').onclick = keAkhir;
  el.querySelector('.promo-tutup').onclick = tutup;
  el.addEventListener('click', e => { if (e.target === el) tutup(); });
  // Esc ditangkap di fase capture supaya tidak ikut menutup layar di bawahnya.
  const tombolEsc = e => { if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); tutup(); } };
  document.addEventListener('keydown', tombolEsc, true);
  ke(0);
  el.querySelector('.promo-tutup').focus();
  return { tutup };
}
